
import Vue from 'vue';
import { mapActions, mapGetters, mapState } from 'vuex';
import Product from '@/models/Product';
import WeightRule from '@/models/WeightRule';
import Cart from '@/models/Cart';
import User from '@/models/User';
import StoreSettings from '@/models/StoreSettings';
import PartnerSettings from '@/models/PartnerSettings';
import MoneyFormat from '@/components/UI/Money/UIMoneyFormat.vue';
import WsaButton from '@/components/UI/Button/wsa-button.vue';
import routerManager from '@/routerManagerNew/routerManager';
import Authentication from '@/views/Authentication/Authentication.vue';
import AddressSelection from '@/components/AddressSelection.vue';
import { ERedirectTo } from '@/enums/ERedirectTo';

export default Vue.extend({

    components: {
        "money-format": MoneyFormat,
        "wsa-button": WsaButton,
        "authentication": Authentication,
        "address-selection": AddressSelection
    },

    props: {
        item: {
            required: true,
            type: Object as () => Product
        }
    },

    data: () => ({
        selectedQuantity: null as number | null,
        isCustomSelected: false as boolean,
        customQtyText: "" as string,
        customQtyError: "" as string,
        authenticationDialog: false as boolean,
        isAddressSelectionDialogDisplayed: false as boolean,
        redirectToCheckoutPageValue: ERedirectTo.Checkout,
        loading: false as boolean
    }),

    computed: {
        ...mapState("partner", ["partnerSettings"]),
        ...mapState("ui", ["isMobileViewDisplayed"]),
        ...mapState("store", ["selectedStoreDetails"]),
        ...mapGetters("cart", ["currentSelectedCart", "currentSelectedCartTotalAmount"]),
        ...mapGetters("auth", ["currentSelectedUser"]),

        user(): User {
            return (this as any).currentSelectedUser as User;
        },

        weightRule(): WeightRule {
            return (this.item as Product).weightRule as WeightRule;
        },

        suffix(): string {
            var rawSuffix = Product.getServiceQuantitySuffix(this.item as Product);
            return rawSuffix.toLowerCase() === "liter" || rawSuffix.toLowerCase() === "litre" ? "L" : rawSuffix;
        },

        unitPrice(): number {
            var product = this.item as Product;
            return product.adjPrice > 0 ? product.adjPrice : product.price;
        },

        totalPrice(): number {
            return (this.selectedQuantity ?? 0) * this.unitPrice;
        },

        isQuantityValid(): boolean {
            return this.selectedQuantity != null
                && this.selectedQuantity >= this.weightRule.minWeight
                && this.selectedQuantity <= this.weightRule.maxWeight;
        },

        itemInCartQuantity(): number {
            var cart: Cart = (this as any).currentSelectedCart;
            var itemFoundInCart = cart?.items.find(i => i.id == (this.item as Product).id);
            return itemFoundInCart ? itemFoundInCart.quantity : 0;
        },

        isItemInCart(): boolean {
            return this.itemInCartQuantity > 0;
        }
    },

    methods: {
        ...mapActions("cart", ["setServiceItemQuantity"]),
        ...mapActions("order", ["getServiceFeeAmount"]),
        ...mapActions("eWallet", ["getEWallet"]),
        ...mapActions("loyalty", ["getLoyalty"]),

        selectPredefinedOption(option: number) {
            this.selectedQuantity = option;
            this.isCustomSelected = false;
            this.customQtyText = option.toString();
            this.customQtyError = "";
        },

        onCustomQtyInput(value: string) {
            this.isCustomSelected = true;
            var parsed = Number.parseFloat(value);

            if(value === "" || Number.isNaN(parsed)) {
                this.customQtyError = "";
                this.selectedQuantity = null;
                return;
            }

            if(parsed < this.weightRule.minWeight || parsed > this.weightRule.maxWeight) {
                this.customQtyError = `Enter a value between ${this.weightRule.minWeight} and ${this.weightRule.maxWeight} ${this.suffix}`;
                this.selectedQuantity = null;
                return;
            }

            this.customQtyError = "";
            this.selectedQuantity = parsed;
        },

        onCustomQtyBlur() {
            if(this.isQuantityValid == false && this.isCustomSelected == true) {
                this.setDefaultQuantity();
            }
        },

        setDefaultQuantity() {
            var rule = this.weightRule;

            if(rule.options.length > 0) {
                this.selectPredefinedOption(rule.options[0]);
            }

            else if(rule.isCustomOptionAllowed == true) {
                this.isCustomSelected = true;
                this.selectedQuantity = rule.minWeight;
                this.customQtyText = rule.minWeight.toString();
                this.customQtyError = "";
            }
        },

        addOrUpdateCart() {
            if(this.isQuantityValid == false || this.selectedQuantity == null)
                return;

            this.setServiceItemQuantity({ product: this.item, quantity: this.selectedQuantity });
            this.goToCheckout();
        },

        // mirrors Cart.vue's goToCheckout()/goToCheckoutFlow() so "Proceed" behaves exactly like the
        // normal "Go To Checkout" button — same login and address pre-flight checks before navigating
        goToCheckout() {
            this.isAddressSelectionDialogDisplayed = false;

            if(typeof this.user.isUserLoggedIn == "undefined" || this.user.isUserLoggedIn == false) {
                this.authenticationDialog = true;
            }

            else if(this.user.usedAddress.isDefault) {
                this.isAddressSelectionDialogDisplayed = true;
            }

            else {
                this.goToCheckoutFlow();
            }
        },

        async goToCheckoutFlow() {
            if(this.user.usedAddress.isDefault) {
                this.isAddressSelectionDialogDisplayed = true;
                return;
            }

            this.loading = true;

            var storeDetails = this.selectedStoreDetails as StoreSettings;

            await this.getServiceFeeAmount({
                basketValue: (this as any).currentSelectedCartTotalAmount,
                branchId: storeDetails.id,
                currencyCode: storeDetails.defaultCurrency.currencyCode
            });

            if((this.partnerSettings as PartnerSettings).isEWalletFeatureEnabled == true && (this.partnerSettings as PartnerSettings).isEWalletPaymentFeatureEnabled == true)
                await this.getEWallet({ currencyId: storeDetails.defaultCurrency.id });

            if((this.partnerSettings as PartnerSettings).isLoyaltyProgramEnabled == true && (this.partnerSettings as PartnerSettings).isPartnerLoyaltyPaymentFeatureEnabled == true)
                await this.getLoyalty({ CashCurrencyCode: storeDetails.defaultCurrency.currencyCode });

            this.loading = false;
            routerManager.goToCheckoutPage();
        }
    },

    mounted() {
        var cartQuantity = this.itemInCartQuantity;

        if(cartQuantity > 0) {
            if(this.weightRule.options.includes(cartQuantity)) {
                this.selectPredefinedOption(cartQuantity);
            }

            else {
                this.isCustomSelected = true;
                this.selectedQuantity = cartQuantity;
                this.customQtyText = cartQuantity.toString();
            }
        }

        else {
            this.setDefaultQuantity();
        }
    }
});
